/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description React Hook useMetaData
 */
import { useCallback, useMemo } from 'react';
import _ from 'lodash';

import usePaginatedProviders from './admin/usePaginatedProviders';
import usePaginatedLocations from './admin/usePaginatedLocations';
import useSelectedPayers from './admin/useSelectedPayers';
import useServiceTypes from './eligibility/useServiceTypes';
import useRegisteredPayers from './admin/useRegisteredPayers';
import useTenantPayers from './payer/useTenantPayers';

import { trimValue } from 'utils/admin/providers';
import {
	getActiveAndEkycCompletedProviders,
	getActiveLocations,
	getClaimStatusPayers,
	getClaimStatusSelectedPayersOptions,
	getEligibilityPayers,
	getEligibilitySelectedPayersOptions,
	getLocationMetaData,
	getProviderMetaData,
	getSelectedActiveClaimStatusPayers,
	getSelectedActiveEligibilityPayers,
	getSelectedEligibilityPayers,
	prepareEligibilityPayerGroupOptions,
	prepareLocationGroupOptions,
	preparePreviousRequestProviderGroupOptions,
	prepareProviderGroupOptions,
} from 'utils/metadata';
import { prepareAllPayersMeta, prepareSelectedPayersMeta } from 'utils/admin/payers';
import { useAppStoreValues } from 'store/app';
import { PayerStatus } from '@veritable/common/adminApi';

/**
 * metadata Hook
 * @returns useMetaData
 */
const useMetaData = (): Metadata.IResponse => {
	const { isARCustomer } = useAppStoreValues().userAccess;
	const { data: providers, ...providerQuery } = usePaginatedProviders({ allProviders: true });
	const { data: serviceLocations, ...locationQuery } = usePaginatedLocations({ allLocations: true });
	const { data: serviceTypes, ...serviceTypesQuery } = useServiceTypes();
	const registeredPayersCall = useRegisteredPayers();
	const selectedPayersCall = useSelectedPayers();
	const { data: tenantPayers = [], ...tenantPayersQuery } = useTenantPayers();
	const { data: allPayers, ...allPayersQuery } = registeredPayersCall;

	const { data: selectedPayers, ...selectedPayersQuery } = isARCustomer ? registeredPayersCall : selectedPayersCall;

	const isLoading = useMemo(
		() =>
			providerQuery.isLoading ||
			locationQuery.isLoading ||
			selectedPayersQuery.isLoading ||
			allPayersQuery.isLoading ||
			tenantPayersQuery.isLoading ||
			serviceTypesQuery.isLoading,
		[
			providerQuery.isLoading,
			locationQuery.isLoading,
			selectedPayersQuery.isLoading,
			allPayersQuery.isLoading,
			serviceTypesQuery.isLoading,
			tenantPayersQuery.isLoading,
		],
	);

	const isFetching = useMemo(
		() =>
			providerQuery.isFetching ||
			locationQuery.isFetching ||
			selectedPayersQuery.isFetching ||
			allPayersQuery.isFetching ||
			serviceTypesQuery.isFetching ||
			tenantPayersQuery.isFetching,
		[
			providerQuery.isFetching,
			locationQuery.isFetching,
			selectedPayersQuery.isFetching,
			allPayersQuery.isFetching,
			serviceTypesQuery.isFetching,
			tenantPayersQuery.isFetching,
		],
	);

	const isError = useMemo(
		() =>
			providerQuery.isError ||
			locationQuery.isError ||
			selectedPayersQuery.isError ||
			allPayersQuery.isError ||
			serviceTypesQuery.isError ||
			tenantPayersQuery.isError,
		[
			providerQuery.isError,
			locationQuery.isError,
			selectedPayersQuery.isError,
			allPayersQuery.isError,
			serviceTypesQuery.isError,
			tenantPayersQuery.isError,
		],
	);

	const refetch = useCallback(() => {
		providerQuery.refetch();
		locationQuery.refetch();
		selectedPayersQuery.refetch();
		allPayersQuery.refetch();
		serviceTypesQuery.refetch();
		tenantPayersQuery.refetch();
	}, [
		providerQuery.refetch,
		locationQuery.refetch,
		selectedPayersQuery.refetch,
		allPayersQuery.refetch,
		serviceTypesQuery.refetch,
		tenantPayersQuery.refetch,
	]);

	const updatedSelectedPayers = useMemo(() => {
		return _.map(tenantPayers, (payer) => {
			const selectedPayer = _.find(selectedPayers, { id: payer.payerId });
			return {
				status: PayerStatus.NUMBER_0,
				...selectedPayer,
				...payer,
			};
		});
	}, [selectedPayers, tenantPayers]);

	/* ======== Provider ========*/
	const providerMeta = useMemo(() => {
		// providers id v/s name mapping
		const providerNames: Record<string, string> = {};
		const trimmedProviderNames: Record<string, string> = {};
		const providerOptions: IMultiSelectOption[] = [];
		// npi numbers list
		const npiNumbers: string[] = [];

		_.forEach(providers?.data, (provider) => {
			providerNames[provider.npi] = provider.name ?? '';
			trimmedProviderNames[provider.npi] = trimValue(provider.name ?? '');
			npiNumbers.push(provider.npi);
			providerOptions.push({ value: provider.id, label: provider.name });
		});

		// whole npi meta data
		const providerMetaData = getProviderMetaData(providers?.data || []);
		return {
			providers: providers?.data || [],
			providerNames,
			trimmedProviderNames,
			providerOptions,
			npiNumbers,
			providerMetaData,
			activeAndEkycCompletedProviders: getActiveAndEkycCompletedProviders(providerMetaData),
			providerGroupOptions: prepareProviderGroupOptions(providerMetaData),
			previousRequestProviderGroupOptions: preparePreviousRequestProviderGroupOptions(providerMetaData),
		};
	}, [providers?.data]);

	/* ======== Location ========*/
	const locationMeta = useMemo(() => {
		// service location id v/s name mapping
		const locationNames: Record<string, string> = {};
		const trimmedLocationNames: Record<string, string> = {};
		// simple location names vs id options for select component
		const locationOptions: IMultiSelectOption[] = [];
		_.forEach(serviceLocations?.data, (loc) => {
			locationNames[loc?.id] = loc.name ?? '';
			trimmedLocationNames[loc?.id] = trimValue(loc.name ?? '');
			locationOptions.push({ value: loc?.id, label: loc.name });
		});
		// whole location meta data
		const locationMetaData = getLocationMetaData(serviceLocations?.data || []);
		const activeLocations = getActiveLocations(locationMetaData);
		const activeLocationOptions = _.map(activeLocations, (location) => ({
			label: location.name,
			value: location.serviceLocationId,
		}));

		return {
			serviceLocations: serviceLocations?.data || [],
			isSingleLocation: _.isNumber(serviceLocations?.count) && serviceLocations?.count <= 1,
			locationNames,
			trimmedLocationNames,
			locationOptions,
			activeLocations,
			activeLocationOptions,
			locationGroupOptions: prepareLocationGroupOptions(locationMetaData),
			previousRequestLocationGroupOptions: prepareLocationGroupOptions(locationMetaData, true),
		};
	}, [serviceLocations?.data]);

	/* ======== Payer ========*/
	const payerMeta = useMemo(() => {
		const allPayersMeta = prepareAllPayersMeta(allPayers || []);
		const selectedPayersMeta = prepareSelectedPayersMeta(updatedSelectedPayers || []);
		return {
			// all payers
			allPayers: allPayersMeta,
			selectedPayers: selectedPayersMeta,
			allPayersMapping: _.keyBy(allPayersMeta, ({ id }) => id),
			// eligibility
			selectedEligibilityPayerOptions: getEligibilitySelectedPayersOptions(selectedPayersMeta),
			selectedActiveEligibilityPayers: getSelectedActiveEligibilityPayers(selectedPayersMeta),
			allEligibilityPayersMeta: getEligibilityPayers(allPayersMeta || []),
			eligibilitySelectedPayersMeta: getSelectedEligibilityPayers(selectedPayersMeta),
			eligibilityPayerGroupOptions: prepareEligibilityPayerGroupOptions(allPayersMeta, selectedPayersMeta),
			// claim status
			selectedActiveClaimStatusPayers: getSelectedActiveClaimStatusPayers(selectedPayersMeta),
			selectedClaimStatusPayerOptions: getClaimStatusSelectedPayersOptions(selectedPayersMeta),
			allClaimStatusPayersMeta: getClaimStatusPayers(allPayersMeta || []),
		};
	}, [updatedSelectedPayers, allPayers, tenantPayers]);

	/* ======== Service Type ========*/
	const serviceTypesMeta = useMemo(() => {
		return {
			serviceTypesOptions: _.map(serviceTypes, (item): IMultiSelectOption => {
				return { label: `${item.name} (${item.code})`, value: item.code };
			}),
		};
	}, [serviceTypes]);

	return useMemo(
		() => ({
			isLoading,
			isFetching,
			isError,
			refetch,
			...providerMeta,
			...locationMeta,
			...payerMeta,
			...serviceTypesMeta,
			tenantPayers,
			tenantPayerOptions: _.map(tenantPayers, (payer) => ({ value: payer?.id, label: payer?.name })),
			tenantPayersMap: _.keyBy(tenantPayers, 'id'),
		}),
		[isLoading, isFetching, isError, refetch, providerMeta, locationMeta, payerMeta, serviceTypesMeta, tenantPayers],
	);
};

export default useMetaData;
